import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, Loader2, Search, CalendarDays, LayoutGrid, List as ListIcon,
  Sparkles, AlertTriangle, ExternalLink, UserPlus, RotateCcw, ShieldCheck, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-700',
  filled: 'bg-indigo-100 text-indigo-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-200 text-slate-700',
  cancelled: 'bg-rose-100 text-rose-700',
  archived: 'bg-zinc-200 text-zinc-600',
};

const StatusBadge = ({ status }) => (
  <Badge className={`border-0 text-[11px] capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {(status || '').replace('_', ' ')}
  </Badge>
);

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

export default function EventHistory() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ q: '', talent: '', state: '', date_from: '', date_to: '', status: 'all' });
  const [includeTest, setIncludeTest] = useState(false);
  const [view, setView] = useState('list');
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Evie
  const [evieMsg, setEvieMsg] = useState('');
  const [evieLoading, setEvieLoading] = useState(false);
  const [evieResult, setEvieResult] = useState(null);

  // Detail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Recovery modal
  const [recovery, setRecovery] = useState(null); // {shift_id, talent, ...}

  const runSearch = useCallback(async (f = filters, inc = includeTest) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(f).forEach(([k, v]) => { if (v && v !== 'all') qs.set(k, v); });
      qs.set('include_test', inc);
      const r = await api.get(`/admin/event-history/search?${qs.toString()}`);
      setEvents(r.data.events || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error('Search failed');
    } finally { setLoading(false); }
  }, [filters, includeTest]);

  const openDetail = useCallback(async (shiftId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const r = await api.get(`/admin/event-history/event/${shiftId}`);
      setDetail(r.data);
    } catch (e) {
      toast.error('Could not load event');
      setDetail(null);
    } finally { setDetailLoading(false); }
  }, []);

  useEffect(() => { runSearch(); /* initial */ // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const ev = params.get('event');
    if (ev) openDetail(ev);
    // eslint-disable-next-line
  }, [params]);

  const askEvie = async () => {
    if (!evieMsg.trim()) return;
    setEvieLoading(true);
    setEvieResult(null);
    try {
      const r = await api.post('/admin/evie/search', { message: evieMsg, include_test: includeTest });
      setEvieResult(r.data);
    } catch (e) {
      toast.error('Evie search failed');
    } finally { setEvieLoading(false); }
  };

  const startRecoveryFromProposal = (p) => {
    if (p.type === 'uncancel_booking') {
      setRecovery({ mode: 'uncancel', shift_id: p.shift_id, booking_id: p.payload.booking_id, talent_name: p.talent_name });
    } else {
      setRecovery({ mode: 'restore', shift_id: p.shift_id, talent: { id: p.payload.talent_id, name: p.talent_name }, allow_update: p.payload.allow_update || false });
    }
  };

  const dismissProposal = async (p) => {
    if (!p.payload?.talent_id) { toast.error('No talent to mark'); return; }
    const reason = window.prompt(`Confirm ${p.talent_name} did NOT work ${p.date}? Add a note (optional):`, '');
    if (reason === null) return;
    try {
      await api.post(`/admin/event-history/event/${p.shift_id}/exclude-talent`, { talent_id: p.payload.talent_id, reason });
      toast.success('Marked as “did not work” — Evie won’t suggest this day again');
      askEvie();
    } catch (e) { toast.error('Could not update'); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-history-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate('/admin/home')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-indigo-600" /> Event History &amp; Recovery
          </h1>
          <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="include-test-toggle">
            <Switch checked={includeTest} onCheckedChange={(v) => { setIncludeTest(v); runSearch(filters, v); }} />
            Include test data
          </label>
        </div>
        <p className="text-sm text-slate-500 mb-5">Complete history — active, completed, closed, cancelled &amp; archived. Nothing disappears once closed.</p>

        {/* Ask Evie */}
        <Card className="mb-5 border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2 text-indigo-700 font-semibold text-sm">
              <Sparkles className="w-4 h-4" /> Ask Evie to find &amp; fix a booking
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                data-testid="evie-input"
                placeholder="e.g. Find Crystal Singleton's Guardian Garage booking at Cobb Galleria Centre"
                value={evieMsg}
                onChange={(e) => setEvieMsg(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && askEvie()}
              />
              <Button onClick={askEvie} disabled={evieLoading} data-testid="evie-ask-btn" className="bg-indigo-600 hover:bg-indigo-700">
                {evieLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Ask Evie
              </Button>
            </div>
            {evieResult && (
              <div className="mt-3" data-testid="evie-result">
                <div className="text-sm text-slate-700 bg-white border border-indigo-100 rounded-lg p-3">{evieResult.reply}</div>
                {(evieResult.proposed_corrections || []).length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Proposed corrections</div>
                    {evieResult.proposed_corrections.map((p, i) => (
                      <div key={i} className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`evie-proposal-${i}`}>
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1 text-sm text-slate-700">{p.description}</div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => startRecoveryFromProposal(p)} data-testid={`evie-fix-btn-${i}`}>
                            Review &amp; Approve
                          </Button>
                          {p.type === 'restore_talent' && p.payload?.talent_id && (
                            <button onClick={() => dismissProposal(p)} className="text-[11px] text-slate-400 hover:text-slate-600 underline" data-testid={`evie-dismiss-btn-${i}`}>
                              Didn't work this day
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(evieResult.matches || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {evieResult.matches.slice(0, 12).map((m) => (
                      <button key={m.shift_id} onClick={() => openDetail(m.shift_id)} className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1 hover:border-indigo-300" data-testid={`evie-match-${m.shift_id}`}>
                        {fmtDate(m.date)} · {m.venue_name || m.event_title || 'Event'} <StatusBadge status={m.status} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-xs">Search</Label>
              <Input data-testid="filter-q" placeholder="Event, venue, client, city, role" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
            </div>
            <div>
              <Label className="text-xs">Talent</Label>
              <Input data-testid="filter-talent" placeholder="Talent name" value={filters.talent} onChange={(e) => setFilters({ ...filters, talent: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input data-testid="filter-state" placeholder="GA" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input data-testid="filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input data-testid="filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <div className="sm:col-span-2 lg:col-span-6 flex items-center justify-between">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {[['list', ListIcon], ['cards', LayoutGrid], ['calendar', CalendarDays]].map(([v, Icon]) => (
                  <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-1 ${view === v ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500'}`} data-testid={`view-${v}`}>
                    <Icon className="w-3.5 h-3.5" /> {v[0].toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
              <Button onClick={() => runSearch()} disabled={loading} data-testid="apply-filters-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-xs text-slate-500 mb-2" data-testid="results-count">{total} event(s) found</div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">No events match your search.</div>
        ) : view === 'list' ? (
          <ListView events={events} onOpen={openDetail} />
        ) : view === 'cards' ? (
          <CardsView events={events} onOpen={openDetail} />
        ) : (
          <CalendarView events={events} onOpen={openDetail} />
        )}
      </div>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); if (params.get('event')) { params.delete('event'); setParams(params); } } }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="event-detail-modal">
          {detailLoading || detail?.loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
          ) : detail?.event ? (
            <EventDetail detail={detail} onRestore={(t) => setRecovery({ mode: 'restore', shift_id: detail.event.id, talent: t })} onUncancel={(bk) => setRecovery({ mode: 'uncancel', shift_id: detail.event.id, booking_id: bk.id, talent_name: bk.talent_name })} onAddTalent={() => setRecovery({ mode: 'restore', shift_id: detail.event.id, talent: null })} />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Recovery modal */}
      {recovery && (
        <RecoveryModal
          recovery={recovery}
          includeTest={includeTest}
          onClose={() => setRecovery(null)}
          onDone={() => { setRecovery(null); if (detail?.event) openDetail(detail.event.id); if (evieResult) askEvie(); runSearch(); }}
        />
      )}
    </div>
  );
}

/* ---------- Views ---------- */
const EventRow = ({ e, onOpen }) => (
  <button onClick={() => onOpen(e.id)} className="w-full text-left grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-slate-100 hover:bg-slate-50" data-testid={`event-row-${e.id}`}>
    <div className="col-span-3 text-sm font-medium text-slate-800">{fmtDate(e.shift_date)}</div>
    <div className="col-span-4 text-sm text-slate-700 truncate">{e.event_title || e.venue_name || 'Event'}</div>
    <div className="col-span-2 text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{e.city ? `${e.city}, ${e.state}` : (e.state || '—')}</div>
    <div className="col-span-2 text-xs text-slate-500 truncate">{e.company_name || '—'}</div>
    <div className="col-span-1 flex justify-end"><StatusBadge status={e.status} /></div>
  </button>
);

const ListView = ({ events, onOpen }) => (
  <Card><CardContent className="p-0">
    <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
      <div className="col-span-3">Date</div><div className="col-span-4">Event</div><div className="col-span-2">Location</div><div className="col-span-2">Client</div><div className="col-span-1 text-right">Status</div>
    </div>
    {events.map((e) => <EventRow key={e.id} e={e} onOpen={onOpen} />)}
  </CardContent></Card>
);

const CardsView = ({ events, onOpen }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {events.map((e) => (
      <button key={e.id} onClick={() => onOpen(e.id)} className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-sm transition" data-testid={`event-card-${e.id}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-500">{fmtDate(e.shift_date)}</span>
          <StatusBadge status={e.status} />
        </div>
        <div className="font-semibold text-slate-800 text-sm mb-1 truncate">{e.event_title || e.venue_name || 'Event'}</div>
        <div className="text-xs text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.venue_name}{e.city ? ` · ${e.city}, ${e.state}` : ''}</div>
        <div className="text-xs text-slate-400 mt-2">{e.company_name || '—'} · {e.role_name}</div>
        <div className="text-[11px] text-slate-400 mt-1">{e.staffed_count} staffed{e.cancelled_count ? ` · ${e.cancelled_count} cancelled` : ''}</div>
      </button>
    ))}
  </div>
);

const CalendarView = ({ events, onOpen }) => {
  const byMonth = {};
  events.forEach((e) => {
    const key = (e.shift_date || '').slice(0, 7);
    (byMonth[key] = byMonth[key] || []).push(e);
  });
  const months = Object.keys(byMonth).sort().reverse();
  return (
    <div className="space-y-5">
      {months.map((m) => (
        <div key={m}>
          <div className="text-sm font-semibold text-slate-700 mb-2">
            {new Date(m + '-01T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {byMonth[m].map((e) => (
              <button key={e.id} onClick={() => onOpen(e.id)} className="text-left bg-white border border-slate-200 rounded-lg p-2.5 hover:border-indigo-300" data-testid={`cal-event-${e.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-800">{new Date(e.shift_date + 'T00:00:00').getDate()}</span>
                  <StatusBadge status={e.status} />
                </div>
                <div className="text-[11px] text-slate-600 truncate">{e.event_title || e.venue_name}</div>
                <div className="text-[10px] text-slate-400 truncate">{e.city}, {e.state}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ---------- Event detail ---------- */
const EventDetail = ({ detail, onRestore, onUncancel, onAddTalent }) => {
  const { event, roster, siblings, audit } = detail;
  return (
    <div data-testid="event-detail-content">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {event.event_title || event.venue_name} <StatusBadge status={event.status} />
          {event.is_test && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">TEST</Badge>}
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mt-2 mb-4">
        <div><span className="text-slate-400">Date:</span> {fmtDate(event.shift_date)}</div>
        <div><span className="text-slate-400">Time:</span> {event.start_time}–{event.end_time} ({event.timezone})</div>
        <div><span className="text-slate-400">Venue:</span> {event.venue_name}</div>
        <div><span className="text-slate-400">Location:</span> {event.city}, {event.state}</div>
        <div><span className="text-slate-400">Client:</span> {event.company_name || event.client_email}</div>
        <div><span className="text-slate-400">Role:</span> {event.role_name}</div>
      </div>

      {siblings?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Other days of this event</div>
          <div className="flex flex-wrap gap-2">
            {siblings.map((s) => (
              <a key={s.id} href={`/admin/event-history?event=${s.id}`} className="text-xs bg-slate-100 rounded-full px-3 py-1 hover:bg-slate-200 flex items-center gap-1" data-testid={`sibling-${s.id}`}>
                {fmtDate(s.shift_date)} <StatusBadge status={s.status} />
              </a>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-500 uppercase">Roster</div>
        <Button size="sm" variant="outline" onClick={onAddTalent} data-testid="add-talent-btn"><UserPlus className="w-3.5 h-3.5 mr-1" /> Add / Restore talent</Button>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden mb-4">
        {roster.length === 0 ? (
          <div className="text-sm text-slate-400 p-4 text-center">No talent booked on this event.</div>
        ) : roster.map((r) => (
          <div key={r.id} className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-0" data-testid={`roster-${r.id}`}>
            <div>
              <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                {r.talent_name} <StatusBadge status={r.status} />
                {r.talent_is_test && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">TEST</Badge>}
                {r.has_payout && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] flex items-center gap-0.5"><ShieldCheck className="w-3 h-3" />Paid</Badge>}
              </div>
              <div className="text-xs text-slate-500">
                {r.clock_in_local ? `${r.clock_in_local} → ${r.clock_out_local}` : 'No attendance'}
                {r.hours_worked != null ? ` · ${r.hours_worked}h` : ''}
                {r.total_pay != null ? ` · $${r.total_pay.toFixed(2)}` : ''}
              </div>
            </div>
            <div className="flex gap-2">
              {r.status === 'cancelled' && !r.has_payout && (
                <Button size="sm" variant="outline" onClick={() => onUncancel({ id: r.id, talent_name: r.talent_name })} data-testid={`uncancel-btn-${r.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Un-cancel
                </Button>
              )}
              {!r.has_payout && (
                <Button size="sm" variant="ghost" onClick={() => onRestore({ id: r.talent_id, name: r.talent_name, existing: true })} data-testid={`edit-attendance-btn-${r.id}`}>
                  Edit attendance
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {audit?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Correction history</div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {audit.map((a, i) => (
              <div key={i} className="text-xs text-slate-500 flex gap-2" data-testid={`audit-${i}`}>
                <span className="text-slate-400">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                <span className="font-medium text-slate-600">{a.action.replace('event_history_', '')}</span>
                <span className="truncate">{a.details?.reason || ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Recovery modal (preview -> confirm) ---------- */
const RecoveryModal = ({ recovery, includeTest, onClose, onDone }) => {
  const isUncancel = recovery.mode === 'uncancel';
  const [talentQuery, setTalentQuery] = useState('');
  const [talentOpts, setTalentOpts] = useState([]);
  const [talent, setTalent] = useState(recovery.talent || null);
  const [form, setForm] = useState({ clock_in_local: '', clock_out_local: '', pay_rate: '', hours_override: '', mark_approved: true });
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);

  const searchTalent = async (q) => {
    setTalentQuery(q);
    if (q.length < 2) { setTalentOpts([]); return; }
    try {
      const r = await api.get(`/admin/event-history/talent-search?q=${encodeURIComponent(q)}&include_test=${includeTest}`);
      setTalentOpts(r.data.talent || []);
    } catch { /* ignore */ }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      if (isUncancel) {
        const r = await api.post(`/admin/event-history/event/${recovery.shift_id}/uncancel-booking`, { booking_id: recovery.booking_id, mode: 'preview' });
        setPreview(r.data.preview);
      } else {
        if (!talent?.id) { toast.error('Select a talent first'); setBusy(false); return; }
        const body = { talent_id: talent.id, mode: 'preview' };
        if (form.clock_in_local) body.clock_in_local = form.clock_in_local;
        if (form.clock_out_local) body.clock_out_local = form.clock_out_local;
        if (form.pay_rate) body.pay_rate = parseFloat(form.pay_rate);
        if (form.hours_override) body.hours_override = parseFloat(form.hours_override);
        body.mark_approved = form.mark_approved;
        const r = await api.post(`/admin/event-history/event/${recovery.shift_id}/restore-talent`, body);
        setPreview(r.data.preview);
        setNeedsUpdate(r.data.preview.action === 'update_existing');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doConfirm = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      if (isUncancel) {
        await api.post(`/admin/event-history/event/${recovery.shift_id}/uncancel-booking`, { booking_id: recovery.booking_id, mode: 'confirm', reason });
      } else {
        const body = { talent_id: talent.id, mode: 'confirm', reason, mark_approved: form.mark_approved, allow_update: needsUpdate || recovery.allow_update || false };
        if (form.clock_in_local) body.clock_in_local = form.clock_in_local;
        if (form.clock_out_local) body.clock_out_local = form.clock_out_local;
        if (form.pay_rate) body.pay_rate = parseFloat(form.pay_rate);
        if (form.hours_override) body.hours_override = parseFloat(form.hours_override);
        await api.post(`/admin/event-history/event/${recovery.shift_id}/restore-talent`, body);
      }
      toast.success('Correction applied');
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply correction');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="recovery-modal">
        <DialogHeader>
          <DialogTitle>{isUncancel ? 'Restore cancelled booking' : 'Add / restore talent attendance'}</DialogTitle>
        </DialogHeader>

        {isUncancel ? (
          <p className="text-sm text-slate-600">Restore <b>{recovery.talent_name}</b>'s cancelled booking. Existing approved hours &amp; pay will be reinstated.</p>
        ) : (
          <div className="space-y-3">
            {!talent ? (
              <div>
                <Label className="text-xs">Find talent</Label>
                <Input data-testid="talent-search-input" value={talentQuery} onChange={(e) => searchTalent(e.target.value)} placeholder="Type a name…" />
                {talentOpts.length > 0 && (
                  <div className="border border-slate-200 rounded-lg mt-1 max-h-40 overflow-y-auto">
                    {talentOpts.map((t) => (
                      <button key={t.id} onClick={() => { setTalent(t); setTalentOpts([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between" data-testid={`talent-opt-${t.id}`}>
                        {t.name} {t.is_test && <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">TEST</Badge>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium">{talent.name}</span>
                {!recovery.talent && <button className="text-xs text-indigo-600" onClick={() => setTalent(null)}>change</button>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Clock-in (venue local)</Label><Input data-testid="clock-in-input" type="time" value={form.clock_in_local} onChange={(e) => setForm({ ...form, clock_in_local: e.target.value })} /></div>
              <div><Label className="text-xs">Clock-out (venue local)</Label><Input data-testid="clock-out-input" type="time" value={form.clock_out_local} onChange={(e) => setForm({ ...form, clock_out_local: e.target.value })} /></div>
              <div><Label className="text-xs">Pay rate ($/hr)</Label><Input data-testid="pay-rate-input" type="number" value={form.pay_rate} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} placeholder="shift rate" /></div>
              <div><Label className="text-xs">Hours override</Label><Input data-testid="hours-override-input" type="number" value={form.hours_override} onChange={(e) => setForm({ ...form, hours_override: e.target.value })} placeholder="auto" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={form.mark_approved} onCheckedChange={(v) => setForm({ ...form, mark_approved: v })} data-testid="mark-approved-switch" />
              Approve timesheet (creates settlement / pay)
            </label>
            <p className="text-[11px] text-slate-400">Leave times blank to use the scheduled shift hours.</p>
          </div>
        )}

        {preview && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm space-y-1 mt-1" data-testid="recovery-preview">
            <div className="font-semibold text-emerald-800">Preview</div>
            {isUncancel ? (
              <>
                <div>Restore status → <b>{preview.restored_status}</b></div>
                {preview.hours_worked != null && <div>{preview.hours_worked}h · ${preview.total_pay?.toFixed(2)}</div>}
              </>
            ) : (
              <>
                <div>{preview.breakdown}</div>
                <div className="text-emerald-700">Total pay: <b>${preview.total_pay?.toFixed(2)}</b> · {preview.mark_approved ? 'will be approved for payout' : 'saved without approval'}</div>
                {(preview.warnings || []).map((w, i) => <div key={i} className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{w}</div>)}
              </>
            )}
          </div>
        )}

        {preview && (
          <div className="mt-1">
            <Label className="text-xs">Reason for this correction (required)</Label>
            <Textarea data-testid="reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Restoring pay for verified completed work" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="recovery-cancel-btn">Cancel</Button>
          {!preview ? (
            <Button onClick={doPreview} disabled={busy} data-testid="recovery-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Preview
            </Button>
          ) : (
            <Button onClick={doConfirm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recovery-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Approve &amp; apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
